import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatFee } from '../utils/format';
import BookingChat from '../components/BookingChat';
import BookingAttachments from '../components/BookingAttachments';

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeTab = searchParams.get('tab') || 'details';

  useEffect(() => {
    async function fetchBooking() {
      try {
        setLoading(true);
        const res = await api.get(`/bookings/${id}`);
        setBooking(res.data.booking);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchBooking();
  }, [id]);

  const setTab = (tab) => {
    setSearchParams({ tab });
  };

  const statusLabels = {
    pending: 'En attente',
    accepted: 'Accepté',
    rejected: 'Refusé',
    cancelled: 'Annulé',
  };

  const statusColors = {
    pending: 'border-status-pending text-status-pending bg-status-pending/10',
    accepted: 'border-accent text-accent bg-accent/10',
    rejected: 'border-status-rejected text-status-rejected bg-status-rejected/10',
    cancelled: 'th-border th-text-muted',
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="h-8 dark:bg-dark-elevated bg-light-elevated w-48 mb-4 animate-pulse" />
        <div className="h-32 th-bg-surface border th-border animate-pulse mt-8" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="th-page-title mb-4">Erreur</h1>
        <p className="th-text-secondary text-sm font-mono">{error || 'Booking non trouvé'}</p>
      </div>
    );
  }

  const isPromoter = user?.id === booking.promoterId;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fadeIn">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="inline-block text-xs font-mono th-text-muted hover:text-accent transition-colors duration-150 mb-4"
      >
        ← Retour au dashboard
      </Link>

      {/* Header */}
      <div className="mb-8">
        <h1 className="th-page-title mb-2">{booking.eventTitle}</h1>
        <div className="flex items-center gap-3 mb-4">
          <p className="th-text-secondary text-sm font-mono">
            {isPromoter ? booking.artistName : (booking.promoterCompanyName || 'Promoteur')}
          </p>
          <span className={`th-badge ${statusColors[booking.status]}`}>
            {statusLabels[booking.status]}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b th-border">
        <button
          onClick={() => setTab('details')}
          className={activeTab === 'details' ? 'th-btn-accent py-2' : 'th-btn py-2'}
        >
          Détails
        </button>
        <button
          onClick={() => setTab('messages')}
          className={activeTab === 'messages' ? 'th-btn-accent py-2' : 'th-btn py-2'}
        >
          Messages
        </button>
        <button
          onClick={() => setTab('files')}
          className={activeTab === 'files' ? 'th-btn-accent py-2' : 'th-btn py-2'}
        >
          Fichiers
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'details' && (
        <div className="space-y-4">
          <section className="border th-border p-6 th-bg-surface">
            <h2 className="th-section-title mb-4">Événement</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs font-mono uppercase tracking-widest th-text-muted">
                  Titre
                </span>
                <span className="text-sm th-text font-mono">{booking.eventTitle}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs font-mono uppercase tracking-widest th-text-muted">
                  Date
                </span>
                <span className="text-sm th-text font-mono">
                  {new Date(booking.eventDate).toLocaleDateString('fr-FR')}
                </span>
              </div>
              {booking.eventCity && (
                <div className="flex justify-between">
                  <span className="text-xs font-mono uppercase tracking-widest th-text-muted">
                    Lieu
                  </span>
                  <span className="text-sm th-text font-mono">{booking.eventVenueName || booking.eventCity}</span>
                </div>
              )}
            </div>
          </section>

          <section className="border th-border p-6 th-bg-surface">
            <h2 className="th-section-title mb-4">Conditions</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs font-mono uppercase tracking-widest th-text-muted">
                  Cachet proposé
                </span>
                <span className="text-sm th-text font-mono">
                  {formatFee(booking.proposedFee, booking.currency)}
                </span>
              </div>
              {booking.acceptedFee && (
                <div className="flex justify-between">
                  <span className="text-xs font-mono uppercase tracking-widest th-text-muted">
                    Cachet accepté
                  </span>
                  <span className="text-sm th-text font-mono">
                    {formatFee(booking.acceptedFee, booking.currency)}
                  </span>
                </div>
              )}
              {booking.technicalRequirements && (
                <div className="pt-2 border-t th-border">
                  <p className="text-xs font-mono uppercase tracking-widest th-text-muted mb-2">
                    Spécifications techniques
                  </p>
                  <p className="text-sm th-text-secondary font-sans">{booking.technicalRequirements}</p>
                </div>
              )}
              <div className="pt-2 border-t th-border flex gap-4">
                {booking.travelIncluded && (
                  <div className="flex items-center gap-1 text-xs font-mono text-accent">
                    <span>✓</span> Transport inclus
                  </div>
                )}
                {booking.accommodationIncluded && (
                  <div className="flex items-center gap-1 text-xs font-mono text-accent">
                    <span>✓</span> Hébergement inclus
                  </div>
                )}
              </div>
            </div>
          </section>

          {isPromoter && booking.notes && (
            <section className="border th-border p-6 th-bg-surface">
              <h2 className="th-section-title mb-4">Notes du promoteur</h2>
              <p className="text-sm th-text-secondary font-sans">{booking.notes}</p>
            </section>
          )}

          {booking.message && !isPromoter && (
            <section className="border th-border p-6 th-bg-surface">
              <h2 className="th-section-title mb-4">Message du promoteur</h2>
              <p className="text-sm th-text-secondary font-sans italic">
                "{booking.message}"
              </p>
            </section>
          )}
        </div>
      )}

      {activeTab === 'messages' && (
        <BookingChat bookingId={id} currentUserId={user?.id} />
      )}

      {activeTab === 'files' && (
        <BookingAttachments bookingId={id} currentUserId={user?.id} />
      )}
    </div>
  );
}
